import { GanttMilestone } from '../../types/gantt.types'
import { MILESTONE_COLORS, MILESTONE_DEFAULT_COLOR } from '../../constants/gantt.const'
import { arrowPath } from '../../constants/gantt.logic'
// 오버레이: 마일스톤 수직선 · 선후관계 화살표/커넥터 SVG · 대기일 막대

export type GanttOverlayProps = {
  milestoneDayMap: Map<number, GanttMilestone[]>
  draggingMsId: number | null
  draggingMsDelta: number
  DW: number
  svgH: number
  showRel: boolean
  sigOn: boolean
  hideBars: boolean
  chartOverlayRef: React.RefObject<SVGSVGElement>
  svgW: number
  arrows: { x0: number; y0: number; x3: number; y3: number; color: string; key: number; pi: number; }[]
  selectedDepId: number | null
  setSelectedDepId: React.Dispatch<React.SetStateAction<number | null>>
  connectorPoints: { taskId: number; edge: "top" | "bottom"; x: number; y: number; color: string; }[]
  isConnectorVisible: (taskId: number, edge: "top" | "bottom") => boolean
  connectDrag: { fromTaskId: number; fromEdge: "top" | "bottom"; x0: number; y0: number; x1: number; y1: number; } | null
  onConnectorMouseDown: (e: React.MouseEvent, taskId: number, edge: "top" | "bottom", x0: number, y0: number) => void
  onConnectorDrop: (targetTaskId: number, targetEdge: "top" | "bottom") => void
  showFloat: boolean
  floatBars: { rowIdx: number; x: number; width: number; key: number; }[]
  rowH: number
  barTop: number
}

export function GanttOverlay({
  milestoneDayMap, draggingMsId, draggingMsDelta, DW, svgH, showRel, sigOn, hideBars, chartOverlayRef, svgW,
  arrows, selectedDepId, setSelectedDepId, connectorPoints, isConnectorVisible, connectDrag,
  onConnectorMouseDown, onConnectorDrop, showFloat, floatBars, rowH, barTop,
}: GanttOverlayProps) {
  return (
    <>
      {Array.from(milestoneDayMap.entries()).map(([day, ms]) =>
        ms.map((m, mi) => {
          const color = MILESTONE_COLORS[m.milestoneType ?? ''] ?? MILESTONE_DEFAULT_COLOR
          const effectiveDay = draggingMsId === m.milestoneId ? day + draggingMsDelta : day
          const x = effectiveDay * DW + Math.floor(DW / 2)
          return (
            <div key={`${m.milestoneId}-${mi}`} style={{ position: 'absolute', top: 0, left: 212 + x, height: svgH, width: 0, zIndex: 1, pointerEvents: 'none', overflow: 'visible', transition: draggingMsId === m.milestoneId ? 'none' : undefined }}>
              {/* 수직 점선 */}
              <div style={{ position: 'absolute', top: 0, left: -0.5, width: 1.5, height: '100%', background: `repeating-linear-gradient(to bottom, ${color} 0, ${color} 4px, transparent 4px, transparent 8px)`, opacity: draggingMsId === m.milestoneId ? 1 : 0.7 }} />
            </div>
          )
        })
      )}

      {/* Dependency arrows + 커넥터(위/아래 원) — 바 숨기기가 켜지면 선후관계 토글과
          무관하게 강제로 숨긴다. arrows.length===0이어도(관계가 아직 하나도 없어도)
          커넥터 원은 보여야 하므로 arrows.length 조건은 걸지 않는다. */}
      {showRel && !sigOn && !hideBars && (
        <svg ref={chartOverlayRef} width={svgW} height={svgH} style={{ position: 'absolute', top: 0, left: 212, pointerEvents: 'none', zIndex: 2, overflow: 'visible' }}>
          {arrows.map(a => {
            const isSelected = selectedDepId === a.key
            return (
              <g key={a.key}>
                {/* 클릭 판정 폭을 넓히는 투명 히트영역 — 실제로 보이는 선(strokeWidth 1.5)만
                    누르기는 어려우니 stroke 10px짜리 투명 선을 겹쳐서 클릭을 받는다. */}
                <path
                  d={arrowPath(a.x0, a.y0, a.x3, a.y3)}
                  stroke="transparent" strokeWidth={10} fill="none"
                  style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
                  onClick={e => { e.stopPropagation(); setSelectedDepId(a.key) }}
                />
                <path
                  d={arrowPath(a.x0, a.y0, a.x3, a.y3)}
                  stroke={isSelected ? 'var(--color-error)' : a.color}
                  strokeWidth={isSelected ? 2.5 : 1.5} fill="none"
                  strokeOpacity={isSelected ? 1 : 0.8}
                  style={{ pointerEvents: 'none' }}
                />
                <circle cx={a.x0} cy={a.y0} r={2.5} fill={isSelected ? 'var(--color-error)' : a.color} fillOpacity={0.8} style={{ pointerEvents: 'none' }} />
                <polygon points={`${a.x3},${a.y3} ${a.x3-7},${a.y3-4} ${a.x3-7},${a.y3+4}`} fill={isSelected ? 'var(--color-error)' : a.color} fillOpacity={0.8} style={{ pointerEvents: 'none' }} />
              </g>
            )
          })}

          {/* 커넥터(위/아래 원) — 평소엔 은은하게 전부 보이고, 드래그 중엔 반대쪽 edge의
              연결 가능한 후보 원(+시작점 자기 자신)만 남기고 나머지는 숨긴다. */}
          {connectorPoints.map(cp => {
            if (!isConnectorVisible(cp.taskId, cp.edge)) return null
            const isOrigin    = !!connectDrag && connectDrag.fromTaskId === cp.taskId && connectDrag.fromEdge === cp.edge
            const isCandidate = !!connectDrag && !isOrigin
            return (
              <circle
                key={`${cp.taskId}-${cp.edge}`}
                cx={cp.x} cy={cp.y}
                r={isOrigin || isCandidate ? 5 : 3.5}
                fill={isCandidate ? 'var(--shell-surface)' : cp.color}
                stroke={isOrigin || isCandidate ? cp.color : 'none'}
                strokeWidth={isOrigin || isCandidate ? 2 : 0}
                fillOpacity={isOrigin || isCandidate ? 1 : 0.5}
                style={{ pointerEvents: connectDrag && !isCandidate ? 'none' : 'all', cursor: 'pointer' }}
                onMouseDown={e => onConnectorMouseDown(e, cp.taskId, cp.edge, cp.x, cp.y)}
                onMouseUp={e => { if (isCandidate) { e.stopPropagation(); onConnectorDrop(cp.taskId, cp.edge) } }}
              />
            )
          })}

          {/* 드래그 중 미리보기 선 */}
          {connectDrag && (
            <path
              d={arrowPath(connectDrag.x0, connectDrag.y0, connectDrag.x1, connectDrag.y1)}
              stroke="var(--gt-primary)" strokeWidth={2} strokeDasharray="4 3" fill="none" style={{ pointerEvents: 'none' }}
            />
          )}
        </svg>
      )}

      {/* 대기일(leadTime) — 그 공정 자신의 바로 이어지는 회색 빈 바(그 공정이 끝난 후
          반드시 대기해야 하는 기간, 후행공정 유무와 무관) — 바 숨기기가 켜지면 대기일
          토글과 무관하게 강제로 숨긴다 */}
      {showFloat && !sigOn && !hideBars && floatBars.map(fb => (
        <div key={fb.key} style={{
          position: 'absolute', top: fb.rowIdx * rowH + barTop, left: 212 + fb.x, width: fb.width, height: 20,
          borderRadius: 4, border: '1px dashed var(--shell-hint)', background: 'var(--shell-skeleton)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
          pointerEvents: 'none', zIndex: 1,
        }}>
          {fb.width >= 24 && <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--shell-on-surface-2)', whiteSpace: 'nowrap' }}>대기일</span>}
        </div>
      ))}
    </>
  )
}
